import { subscribeToAuthChanges } from './authSession';
import { logout, rehydrateSession } from '../store/authSlice';

/**
 * Normalizes an auth state snapshot for deterministic cross-tab comparisons.
 */
export const normalizeAuthSnapshot = (auth) => {
  if (!auth) {
    return {
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      userRole: null
    };
  }

  const role = auth.user?.role;
  return {
    accessToken: auth.accessToken || null,
    refreshToken: auth.refreshToken || null,
    isAuthenticated: Boolean(auth.isAuthenticated),
    userRole: role ? String(role).trim().toLowerCase() : null
  };
};

/**
 * Initializes cross-tab authentication synchronization.
 * Listens to storage and BroadcastChannel events emitted when another tab
 * logs in, refreshes tokens, or logs out, and updates the local Redux store accordingly.
 *
 * @param {Object} store - Redux store instance
 * @returns {Function} Cleanup function to unsubscribe listeners
 */
export const initAuthSync = (store) => {
  if (!store || typeof window === 'undefined') {
    return () => {};
  }

  return subscribeToAuthChanges((eventType, persistedAuth) => {
    // Only process explicit auth events
    if (eventType !== 'SESSION_UPDATED' && eventType !== 'SESSION_CLEARED') {
      return;
    }

    const currentState = store.getState()?.auth || {};

    if (eventType === 'SESSION_UPDATED' && persistedAuth?.isAuthenticated) {
      const normalizedLocal = normalizeAuthSnapshot(currentState);
      const normalizedPersisted = normalizeAuthSnapshot(persistedAuth);

      if (JSON.stringify(normalizedLocal) !== JSON.stringify(normalizedPersisted)) {
        store.dispatch(rehydrateSession());
      }
    } else if (eventType === 'SESSION_CLEARED' || !persistedAuth?.isAuthenticated) {
      // Another tab logged out
      if (currentState.isAuthenticated) {
        store.dispatch(logout());
      }
    }
  });
};
